import { PAPER_TYPES } from './constants';
import { CombinedPaperTypeAndOrientation } from './types/combined-paper-type-and-orientation.type';
import { PaperDimensions } from './types/paper-dimensions.type';
import { PaperTypes } from './types/paper-types.type';
import { UniqueSvgElement } from './unique-svg-element';

export class PaperPreviewHandler extends UniqueSvgElement {
  protected renderPageMargins(
    paperType: CombinedPaperTypeAndOrientation,
    leftMargin: number,
    topMargin: number,
    rightMargin: number,
    bottomMargin: number,
  ) {
    return new Promise<void>(resolve =>
      requestAnimationFrame(() => {
        const paperDimensions = PAPER_TYPES[paperType];
        const { width, height } = paperDimensions;

        this.setWidth(Math.round(width * this.scaleFactor));
        this.setHeight(Math.round(height * this.scaleFactor));
        this.setViewBox(`-1 -1 ${width + 2} ${height + 2}`);

        this.handleEdges(paperDimensions);

        this.handleMargins(
          paperDimensions,
          leftMargin,
          topMargin,
          rightMargin,
          bottomMargin,
        );

        resolve();
      }),
    );
  }

  private handleEdges(paperDimensions: PaperDimensions) {
    this.handleLeftEdge(paperDimensions);
    this.handleTopEdge(paperDimensions);
    this.handleRightEdge(paperDimensions);
    this.handleBottomEdge(paperDimensions);
  }

  private handleLeftEdge({ height }: PaperDimensions) {
    const line = this.getChild<SVGLineElement>('line.left-edge');

    this.setLine(line, 0, 0, 0, height);
  }

  private handleTopEdge({ width }: PaperDimensions) {
    const line = this.getChild<SVGLineElement>('line.top-edge');

    this.setLine(line, 0, 0, width, 0);
  }

  private handleRightEdge({ height, width }: PaperDimensions) {
    const line = this.getChild<SVGLineElement>('line.right-edge');

    this.setLine(line, width, 0, width, height);
  }

  private handleBottomEdge({ height, width }: PaperDimensions) {
    const line = this.getChild<SVGLineElement>('line.bottom-edge');

    this.setLine(line, 0, height, width, height);
  }

  private handleMargins(
    paperDimensions: PaperDimensions,
    leftMargin: number,
    topMargin: number,
    rightMargin: number,
    bottomMargin: number,
  ) {
    this.handleLeftMargin(paperDimensions, leftMargin);
    this.handleTopMargin(paperDimensions, topMargin);
    this.handleRightMargin(paperDimensions, rightMargin);
    this.handleBottomMargin(paperDimensions, bottomMargin);
  }

  private handleLeftMargin(paper: PaperDimensions, leftMargin: number) {
    const line = this.getChild<SVGLineElement>('line.left-margin');

    this.setLine(line, leftMargin, 0, leftMargin, paper.height);
  }

  private handleTopMargin(paper: PaperDimensions, topMargin: number) {
    const line = this.getChild<SVGLineElement>('line.top-margin');

    this.setLine(line, 0, topMargin, paper.width, topMargin);
  }

  private handleRightMargin(paper: PaperDimensions, rightMargin: number) {
    const line = this.getChild<SVGLineElement>('line.right-margin');

    this.setLine(
      line,
      paper.width - rightMargin,
      0,
      paper.width - rightMargin,
      paper.height,
    );
  }

  private handleBottomMargin(paper: PaperDimensions, bottomMargin: number) {
    const line = this.getChild<SVGLineElement>('line.bottom-margin');

    this.setLine(
      line,
      0,
      paper.height - bottomMargin,
      paper.width,
      paper.height - bottomMargin,
    );
  }

  private setLine(
    line: SVGLineElement | null,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) {
    this.setX1(x1, line);
    this.setY1(y1, line);
    this.setX2(x2, line);
    this.setY2(y2, line);
    this.setStrokeWidth(1, line);
  }
}
